import React, { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Grid, TextField } from '@mui/material';

import IPage from '../../interfaces/IPage';
import IFormError from '../../interfaces/IFormError';
import { useToastr } from '../../hooks/useToastr';
import { useAuth } from '../../hooks/auth';
import { useHubSpotPipelineService } from '../../services/useHubSpotPipelineService';
import IHubSpotPipeline from '../../interfaces/IHubSpotPipeline';
import getValidationError from '../../util/getValidationError';
import { TitleContainer } from '../../components/title-container/TitleContainer';
import { PageCard } from '../../components/page-card/PageCard';
import { Form } from '../../components/form/Form';
import { BackdropCustom } from '../../components/backdrop/Backdrop';
import { ButtonGroup } from '../../components/button-group/ButtonGroup';
import { ConfirmationArea } from '../../components/confirmation-area/ConfirmationArea';
import HubSpotPipelineStages from './hub-spot-pipeline-stages/HubSpotPipelineStages';

const HubSpotPipeline: React.FC<IPage> = ({ title }) => {
  window.document.title = title;

  const { signOut } = useAuth();
  const toastr = useToastr();
  const navigate = useNavigate();
  const location = useLocation();

  const [hubSpotPipelineId, setHubSpotPipelineId] = useState('');
  const [hubSpotPipelineIdParams, setHubSpotPipelineIdParams] = useState('');
  const [label, setLabel] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<IFormError>({});

  const { createHubSpotPipeline, updateHubSpotPipeline, deleteHubSpotPipeline, findHubSpotPipeline } =
    useHubSpotPipelineService();

  const handleToHubSpotPipeline = useCallback(() => {
    navigate('/hubspot-pipelines');
  }, [navigate]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    const id = location.pathname.replace('/hubspot-pipeline', '').replace('/', '');

    setLoading(true);
    setFormErrors({});

    try {
      const data = {
        id: hubSpotPipelineId,
        label,
      };

      const schema = Yup.object().shape({
        id: Yup.string().required('ID é obrigatório'),
        label: Yup.string().required('Nome é obrigatório'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      if (id) {
        await updateHubSpotPipeline(hubSpotPipelineId, data)
          .then(response => {
            toastr.success('Pipeline alterado com sucesso');

            response.id && navigate(`/hubspot-pipeline/${response.id}`);
          })
          .catch(error => {
            if (error.statusCode === 401) {
              signOut();
              navigate('/');
            }

            toastr.error(error?.message || 'Erro ao alterar configurações da aplicação');
          });
      } else {
        await createHubSpotPipeline(data)
          .then(response => {
            toastr.success('Pipeline cadastrada com sucesso');

            if (response.id) {
              navigate(`/hubspot-pipeline/${response.id}`);
              window.location.reload();
            }
          })
          .catch(error => {
            if (error.statusCode === 401) {
              signOut();
            }

            toastr.error(error?.message || 'Erro ao cadastrar configurações da aplicação');
          });
      }
    } catch (error: Yup.ValidationError | any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationError(error);
        setFormErrors(errors);
        return;
      }

      toastr.error('Ocorreu um erro ao cadastrar/atualizar o registro');
    } finally {
      setLoading(false);
    }
  }, [hubSpotPipelineId, label, signOut, navigate, handleToHubSpotPipeline]);

  const handleDeleteHubSpotPipeline = useCallback(async () => {
    if (hubSpotPipelineId) {
      setLoading(true);

      await deleteHubSpotPipeline(hubSpotPipelineId)
        .then(async () => {
          toastr.success('Pipeline deletada com sucesso');

          navigate('/hubspot-pipelines');
        })
        .catch(error => {
          if (error.statusCode === 401) {
            signOut();
          }

          toastr.error('Erro ao deletar registro');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [navigate, signOut, hubSpotPipelineId]);

  const handleListHubSpotPipeline = useCallback(async () => {
    const id = location.pathname.replace('/hubspot-pipeline', '').replace('/', '');

    setHubSpotPipelineIdParams(id);
    setHubSpotPipelineId(id);

    if (id) {
      setLoading(true);
      await findHubSpotPipeline(id)
        .then(response => {
          const hubSpotPipeline: IHubSpotPipeline = response;
          setLabel(hubSpotPipeline.label);
        })
        .catch(error => {
          if (error.statusCode === 401) {
            signOut();
          }

          toastr.error(error?.message || 'Contate a equipe de suporte');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [signOut, location.pathname]);

  useEffect(() => {
    handleListHubSpotPipeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TitleContainer>
        <h1>Pipeline</h1>
      </TitleContainer>
      <PageCard>
        <Form>
          {loading && <BackdropCustom />}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: '100%' }}
                size="small"
                label="ID"
                value={hubSpotPipelineId}
                onChange={e => setHubSpotPipelineId(e.target.value)}
                autoComplete="off"
                helperText={formErrors.hubSpotPipelineId}
                error={!!formErrors.hubSpotPipelineId}
                disabled={!!hubSpotPipelineIdParams}
                autoFocus
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: '100%' }}
                size="small"
                label="Nome"
                value={label}
                onChange={e => setLabel(e.target.value)}
                autoComplete="off"
                helperText={formErrors.label}
                error={!!formErrors.label}
              />
            </Grid>
          </Grid>

          <ButtonGroup>
            <Button
              onClick={() => navigate('/hubspot-pipelines')}
              color="inherit"
              disabled={loading}
              variant="contained"
              style={{ textTransform: 'none' }}
            >
              Voltar
            </Button>

            {hubSpotPipelineId && (
              <Button
                onClick={() => {
                  setDeleteDialogOpen(true);
                }}
                disabled={loading}
                color="error"
                variant="contained"
                style={{ marginLeft: '8px', textTransform: 'none' }}
              >
                Excluir
              </Button>
            )}

            <Button
              onClick={() => handleSubmit()}
              disabled={loading}
              title="Confirma o cadastro ou alteração"
              variant="contained"
              style={{ marginLeft: '8px', textTransform: 'none' }}
            >
              Confirmar
            </Button>

            {hubSpotPipelineId && (
              <ConfirmationArea
                id={hubSpotPipelineId}
                dialogOpen={deleteDialogOpen}
                handleConfirmation={handleDeleteHubSpotPipeline}
                title="Deseja realmente excluir a configuração de aplicação?"
                message=""
                deny={() => {
                  setDeleteDialogOpen(false);
                }}
              />
            )}
          </ButtonGroup>
        </Form>
      </PageCard>

      {hubSpotPipelineIdParams && <HubSpotPipelineStages hubspotPipelineId={hubSpotPipelineIdParams} />}
    </>
  );
};

export default HubSpotPipeline;
